import {models, stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common, query } from '@kurtosys/ksys-app-template';
import { IConfigurationRedirectInput } from '@kurtosys/types/app/index.js';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration.js';
import { MockData } from '../../../configuration/mockData/index.js';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { Feature } from '../../shared/Feature.js';
import { IAppComponents } from '../models/IAppComponents.js';
import { IPlaceholderFormat } from '../models/IPlaceholderFormat.js';

type ITextTooltip = components.base.Text.models.ITextTooltip;
type ITextProps = components.base.Text.models.ITextProps;
type ICardTooltip = components.base.Card.models.ICardTooltip;
type ICardProps = components.base.Card.models.ICardProps;

/* [Component: appStoreComponentImport] */

let devConfig: models.IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	@observable commentary: string | undefined;
	@observable commentaryTooltip: ITextTooltip | undefined;

	constructor(
		element: HTMLElement,
		url: string,
		storeContext: TStoreContext,
		manifest: IManifest,
		mockData?: MockData,
	) {
		super(element, url, storeContext, manifest, Feature, devConfig, mockData);
		makeObservable(this);
	}

	@computed
	get hasData(): boolean {
		let hasData = this.isBootstrapped && !common.commonUtils.isNullOrEmpty(this.commentary);
		if (this.appComponentConfiguration) {
			const { noDataOptions } = this.appComponentConfiguration;
			if (noDataOptions) {
				const { customValidation } = noDataOptions;
				if (customValidation) {
					const queryStore =
						this.storeContext.get<stores.QueryStore<IConfiguration, IComponentStyles>>('queryStore');
					const { includeDefaultValidation, validateWithDefaultAs, conditionalValidation } = customValidation;
					const conditionalHelper = new common.helpers.ConditionalHelper(
						{ queryClass: query.Query },
						conditionalValidation,
					);
					const isValid = conditionalHelper.matchesWithOptions({
						executionOptions: queryStore.executionOptions,
					});
					if (includeDefaultValidation) {
						switch (validateWithDefaultAs) {
							case 'ANY':
								hasData = hasData || isValid;
								break;
							case 'BOTH':
							default:
								hasData = hasData && isValid;
								break;
						}
					} else {
						hasData = isValid;
					}
				}
			}
		}
		return hasData;
	}

	@action
	contextsDidUpdateAfter = async () => {
		this.updateObservables();
	};

	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get title(): string | undefined {
		const title = this.getQueryValue(this.appComponentConfiguration && this.appComponentConfiguration.title);
		return this.applyPlaceholderFormatting(title, this.titleFormating);
	}

	@computed
	get titleTooltip(): ICardTooltip | undefined {
		const tooltipQueries = this.appComponentConfiguration && this.appComponentConfiguration.titleTooltip;
		const queryStore = this.storeContext.get<stores.QueryStore<IConfiguration, IComponentStyles>>('queryStore');
		const tooltipProps = query.queryUtils.queryHelper.convertQueryMime(
			{ queryClass: query.Query },
			tooltipQueries,
			queryStore.executionOptions,
		);
		return tooltipProps && { target: 'title', ...tooltipProps };
	}

	@computed
	get titleFormating(): IPlaceholderFormat | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.titleFormating;
	}

	@computed
	get subtitle(): string | undefined {
		const subtitle = this.getQueryValue(this.appComponentConfiguration && this.appComponentConfiguration.subtitle);
		return this.applyPlaceholderFormatting(subtitle, this.subtitleFormating);
	}

	@computed
	get subTitleTooltip(): ICardTooltip | undefined {
		const tooltipQueries = this.appComponentConfiguration && this.appComponentConfiguration.subTitleTooltip;
		const queryStore = this.storeContext.get<stores.QueryStore<IConfiguration, IComponentStyles>>('queryStore');
		const tooltipProps = query.queryUtils.queryHelper.convertQueryMime(
			{ queryClass: query.Query },
			tooltipQueries,
			queryStore.executionOptions,
		);
		return tooltipProps && { target: 'subTitle', ...tooltipProps };
	}

	@computed
	get subtitleFormating(): IPlaceholderFormat | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.subtitleFormating;
	}

	@computed
	get redirectInputsConfiguration(): IConfigurationRedirectInput[] | undefined {
		const appStore = this.storeContext.get<AppStore>('appStore');
		return appStore.configuration && appStore.configuration.redirectInputs;
	}

	@action
	updateObservables() {
		const tooltipQueries = this.appComponentConfiguration && this.appComponentConfiguration.commentaryTooltip;
		const queryStore = this.storeContext.get<stores.QueryStore<IConfiguration, IComponentStyles>>('queryStore');

		this.commentary = this.getQueryValue(
			this.appComponentConfiguration && this.appComponentConfiguration.commentary,
		);
		this.commentaryTooltip = query.queryUtils.queryHelper.convertQueryMime(
			{ queryClass: query.Query },
			tooltipQueries,
			queryStore.executionOptions,
		);
	}

	applyPlaceholderFormatting = (value: any, placeholder?: IPlaceholderFormat) => {
		if (placeholder) {
			const translationStore =
				this.storeContext.get<stores.TranslationStore<IConfiguration, IComponentStyles>>('translationStore');
			const { translate } = translationStore;
			const { format, placeholderKey } = placeholder;
			if (format && placeholder && translate) {
				return translate(format, { [placeholderKey]: value });
			}
		}
		return value;
	};

	@computed
	get cardProps(): ICardProps {
		const props = this.appComponentConfiguration && this.appComponentConfiguration.cardProps;

		const tooltips = [];
		if (this.titleTooltip) {
			tooltips.push(this.titleTooltip);
		}
		if (this.subTitleTooltip) {
			tooltips.push(this.subTitleTooltip);
		}

		const response: ICardProps = {
			tooltips,
			title: this.title,
			subtitle: this.subtitle,
		};

		return common.commonUtils.deepMergeObjects(props || {}, response);
	}

	@computed
	get textProps(): ITextProps {
		const props = this.appComponentConfiguration && this.appComponentConfiguration.textProps;
		const response: ITextProps = {
			value: this.commentary || '',
			tooltip: this.commentaryTooltip,
		};
		const textProps = common.commonUtils.deepMergeObjects(props || {}, response);
		return textProps;
	}

	get globalInputIdentifiers(): string[] {
		return (this.appComponentConfiguration && this.appComponentConfiguration.globalInputIdentifiers) || [];
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	async customInitializeAfter() {
		this.updateObservables();
	}
}
